import { AfterContentInit, Component, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormControl, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { EMPTY, of, switchMap } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { StripeCardNumberComponent, StripeService } from 'ngx-stripe';

import { LoaderService } from '@app/shared';
import { AddressInput, CreditCard, CreditPackType, CurrencyOptions, CustomCoupon, Customer, CustomerInput, Order, PaymentMethodsEnum, Plan, User, PaymentGatewayEnum } from '@app/models';
import { AppService, ErrorHandlerService, NotyService, OrganizationService, PaymentService, UserService } from '@app/services';
import { StripeCardElementOptions, StripeCardNumberElementOptions, StripeElementsOptions } from '@stripe/stripe-js';
import { IuguService } from '@app/services/iugu.service';

export enum EditPaymentInfoModalStep {
  paymentMethod = 'paymentMethod',
  paymentInfo = 'paymentInfo',
  paymentEmail = 'paymentEmail',
  checkout = 'checkout'
}
export interface ModalResult {
  currentCustomer: Customer;
  payableWith: PaymentMethodsEnum;
  cardToken: string;
  couponCode?: string;
}
export interface ModalPublicProperties {
  currentCustomer: Customer;
  currentOrder: Order;
  lockStep?: EditPaymentInfoModalStep;
  plan?: Plan;
  creditsPack?: CreditPackType;
}

@Component({
  selector: 'app-edit-payment-info-modal',
  templateUrl: './edit-payment-info-modal.component.html',
  styleUrls: ['./edit-payment-info-modal.component.scss', '../configurations-plan.component.scss'],
  standalone: false
})
export class EditPaymentInfoModalComponent implements AfterContentInit, OnChanges, OnDestroy {
  @Input() currentCustomer: Customer;
  @Input() currentOrder: Order;
  @Input() lockStep: EditPaymentInfoModalStep;
  @Input() plan: ModalPublicProperties['plan'];
  @Input() creditsPack: ModalPublicProperties['creditsPack'];
  form!: UntypedFormGroup;
  payableWith: PaymentMethodsEnum = PaymentMethodsEnum.CreditCard;
  PaymentMethodsEnum = PaymentMethodsEnum;
  defaultPaymentDataIsCreditCard = false;
  isLegalPerson = true;
  isLoading = false;
  canPurchaseCredits = false;
  currentStep: EditPaymentInfoModalStep;
  country: string;
  currency: CurrencyOptions;

  couponCodeControl = new FormControl('');
  coupon: CustomCoupon;
  isActiveCoupon = false;

  cardToken: string;

  creditCardForm = this.formBuilder.group({
    number: [null],
    verification_value: [null],
    full_name: [null],
    expiration: [null]
  });

  readonly EditPaymentInfoModalStep = EditPaymentInfoModalStep;

  // CARD STRIPE CONFIGS
  public baseStyle = {
    iconColor: '#666EE8',
    color: '#14112d',
    fontWeight: '400',
    fontFamily: 'Lato,-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Helvetica Neue,Arial,Noto Sans,sans-serif,Apple Color Emoji,Segoe UI Emoji,Segoe UI Symbol,Noto Color Emoji',
    fontSize: '14px',
    '::placeholder': { color: '#959cae' }
  };
  public cardOptions: StripeCardElementOptions = {
    style: { base: this.baseStyle }
  };
  public cardNumberOptions: StripeCardNumberElementOptions = {
    iconStyle: 'solid',
    showIcon: true,
    style: { base: this.baseStyle }
  };
  // @ts-ignore
  elementsOptions: StripeElementsOptions = { locale: this.translateService.currentLang };
  @ViewChild(StripeCardNumberComponent) card: StripeCardNumberComponent;
  private lastCouponCode: string | null = null;

  constructor(
    public modal: NgbActiveModal,
    public organizationService: OrganizationService,
    public translateService: TranslateService,
    public appService: AppService,
    private formBuilder: UntypedFormBuilder,
    private errorHandlerService: ErrorHandlerService,
    private notyService: NotyService,
    private loaderService: LoaderService,
    private userService: UserService,
    private paymentService: PaymentService,
    private stripeService: StripeService,
    private iuguService: IuguService
  ) {
    this.form = this.formBuilder.group({
      customer: this.formBuilder.group({ name: null, email: null, cpf: null, cnpj: null } as CustomerInput),
      address: this.formBuilder.group({ zip_code: null, street: null, number: null, complement: null, district: null, city: null, state: null, country: null, ibge_code: null } as AddressInput)
    });
  }

  ngAfterContentInit() {
    this.currentStep = this.lockStep || EditPaymentInfoModalStep.paymentMethod;
    this.refreshForms(this.currentCustomer);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.currentCustomer && changes.currentCustomer.currentValue && changes.currentCustomer.currentValue !== changes.currentCustomer.previousValue) {
      this.refreshForms(changes.currentCustomer.currentValue);
    }
  }

  isIuguGateway(): boolean {
    return this.currency === 'BRL' && this.currentOrder?.gateway === PaymentGatewayEnum.Iugu;
  }

  isStripeGateway(): boolean {
    return this.currency === 'USD' && this.currentOrder?.gateway === PaymentGatewayEnum.Stripe;
  }

  setPayableWith(paymentEnum: PaymentMethodsEnum) {
    this.payableWith = paymentEnum;
  }

  ngOnDestroy() {}

  update() {
    if (!this.creditsPack && !this.plan && this.payableWith === PaymentMethodsEnum.CreditCard && EditPaymentInfoModalStep.paymentMethod === this.currentStep) {
      if (this.country === 'BR') {
        this.createIuguToken(true);
      } else {
        this.createStripeToken(true);
      }
    } else {
      this.submitModal();
    }
  }

  submitModal() {
    const shouldApplyCoupon = this.couponCodeControl.value?.trim() && !this.coupon && !this.creditsPack;
    const coupon$ = shouldApplyCoupon ? this.applyCouponCode() : of(null);
    const setCustomer$ = this.setCustomer().pipe(
      catchError(error => {
        this.errorHandlerService.handle(error);
        return EMPTY;
      })
    );

    this.isLoading = true;
    this.form.markAllAsTouched();
    this.loaderService.show();
    coupon$
      .pipe(
        switchMap(() => (this.lockStep === EditPaymentInfoModalStep.paymentMethod ? of(this.currentCustomer) : setCustomer$)),
        finalize(() => {
          this.isLoading = false;
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: data => {
          this.modal.close({ currentCustomer: data, payableWith: this.payableWith, cardToken: this.cardToken, couponCode: this.coupon?.code } as ModalResult);
        }
      });
  }

  fillAddressByZipCode(zipCode: string) {
    const parsedZipCode = (zipCode || '').replace(/[^0-9]/g, '');
    if (this.isBrl() && parsedZipCode.length === 8) {
      this.appService.getZipCodeInfo(zipCode).subscribe({
        next: info => {
          this.form.get('address.street').setValue(info.logradouro);
          this.form.get('address.district').setValue(info.bairro);
          this.form.get('address.city').setValue(info.localidade);
          this.form.get('address.state').setValue(info.uf);
          this.form.get('address.country').setValue('BR');
          this.form.get('address.ibge_code').setValue(info.ibge);
        },
        error: error => {
          if (typeof error === 'string' && error.match(/invalid/i)) {
            this.form.get('address.zip_code').setErrors({ server: this.translateService.instant('error_invalid_cep') });
            this.form.get('address.zip_code').markAsTouched();
          }
        }
      });
    }
  }

  additionalGroupsCount() {
    return this.organizationService.additionalGroupsCount(this.currentOrder);
  }

  additionalGroupsMonths() {
    return this.organizationService.additionalGroupsMonths(this.plan);
  }

  checkoutTotal() {
    if (this.creditsPack) {
      return ((this.creditsPack.is_promo ? this.creditsPack.amount_promo : this.creditsPack.amount) * this.creditsPack.quantity) / 100 || 0;
    }

    const planAmount = this.plan.amount / 100 + this.additionalGroupsCount() * this.additionalGroupsMonths() * this.organizationService.additionalGroupPrices[this.currency];
    return this.coupon?.discountAmount != null ? planAmount - this.coupon.discountAmount / 100 : planAmount;
  }

  callForToken() {
    if (this.country === 'BR' && this.payableWith === PaymentMethodsEnum.CreditCard) {
      if (this.defaultPaymentDataIsCreditCard) {
        this.currentStep = EditPaymentInfoModalStep.paymentInfo;
      } else {
        this.createIuguToken();
      }
    } else {
      this.createStripeToken();
    }
  }

  createIuguToken(isUpdatePayableWith?: boolean) {
    if (!this.iuguService.isLoaded()) {
      this.iuguService.load().subscribe();
      return;
    }

    this.creditCardForm.markAllAsTouched();
    if (this.creditCardForm.invalid) {
      return;
    }

    const card: CreditCard = this.creditCardForm.value as CreditCard;
    this.isLoading = true;
    this.loaderService.show();
    this.iuguService
      .createPaymentToken(card)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: token => {
          this.cardToken = token;
          if (isUpdatePayableWith) {
            this.submitModal();
            return;
          }
          this.currentStep = EditPaymentInfoModalStep.paymentInfo;
        }
      });
  }

  createStripeToken(isUpdatePayableWith?: boolean) {
    // if stripeCardElement doesn't exist and if the user has a saved card, there's no need to create a new token, the saved card will be used for the charge.
    if (!this.card?.element) {
      this.currentStep = EditPaymentInfoModalStep.paymentInfo;
      return;
    }

    this.isLoading = true;
    this.loaderService.show();
    this.stripeService
      .createToken(this.card.element)
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: data => {
          if (data?.error) {
            this.currentStep = EditPaymentInfoModalStep.paymentMethod;
            this.notyService.error(data.error.message);
            return;
          }

          this.cardToken = data.token.id;
          if (isUpdatePayableWith) {
            this.submitModal();
            return;
          }
          this.currentStep = EditPaymentInfoModalStep.paymentInfo;
        },
        error: error => {
          this.currentStep = EditPaymentInfoModalStep.paymentMethod;
          this.errorHandlerService.handle(error);
        }
      });
  }

  isBrl() {
    return this.currency === 'BRL';
  }

  toggleSwitchPerson(): void {
    if (this.form.get('customer.cpf').invalid) this.form.get('customer.cpf').setValue('');
    if (this.form.get('customer.cnpj').invalid) this.form.get('customer.cnpj').setValue('');
  }

  onEnterCoupon(event: KeyboardEvent): void {
    event.preventDefault();
    this.triggerApplyCouponCode();
  }

  triggerApplyCouponCode() {
    this.applyCouponCode().subscribe();
  }

  applyCouponCode() {
    const code = this.couponCodeControl.value?.trim();

    if (!code || code === this.lastCouponCode) {
      return of(null); // Retorna um observable vazio para manter a consistência
    }

    this.lastCouponCode = code;
    this.isLoading = true;
    this.loaderService.show();

    return this.paymentService.coupon({ code }).pipe(
      tap(coupon => {
        let discountAmount = coupon.amount_off != null ? coupon.amount_off : coupon.percent_off != null ? this.plan.amount * (coupon.percent_off / 100) : 0;
        // Ensure the discount does not exceed plan amount
        if (discountAmount > this.plan.amount) {
          discountAmount = this.plan.amount;
        }

        this.coupon = { ...coupon, discountAmount, code };
        this.couponCodeControl.disable();
        this.notyService.success(this.translateService.instant('notyService.coupon_code_applied'));
      }),
      catchError(error => {
        this.coupon = null;
        this.couponCodeControl.setErrors(error);
        this.notyService.error(this.translateService.instant('notyService.coupon_code_not_found'));
        return EMPTY;
      }),
      finalize(() => {
        this.isLoading = false;
        this.loaderService.hide();
      })
    );
  }

  removeCouponCode() {
    this.coupon = null;
    this.lastCouponCode = null;
    this.couponCodeControl.setValue('');
    this.couponCodeControl.enable();
  }

  private setCustomer() {
    const addressData = this.form.get('address').value;
    const customer = this.form.get('customer').value;

    addressData.number = addressData.number || 'S/N';
    addressData.country = addressData.country || 'BR';
    if (!this.isBrl()) {
      delete customer.cpf;
      delete customer.cnpj;
    } else if (this.isLegalPerson) {
      delete customer.cpf;
    } else {
      delete customer.cnpj;
    }
    const costumer$ = this.currentCustomer?.created_at
      ? this.paymentService.updateCustomer({ address: addressData, customer })
      : this.paymentService.createCustomer({ address: addressData, customer });
    return costumer$;
  }

  private refreshForms(customer: Customer) {
    this.userService
      .getCurrentUser({ fetchPolicy: 'cache-first' })
      .pipe(
        tap(user => {
          this.country = user.organization.country;
          this.currency = this.appService.currencyForCountry(this.country);
          this.payableWith = this.currency === 'BRL' ? this.payableWith : PaymentMethodsEnum.CreditCard;
          this.defaultPaymentDataIsCreditCard = !!this.currentOrder?.card_data?.last_digits && (this.isIuguGateway() || this.isStripeGateway());

          if (this.country === 'BR') {
            this.iuguService.load().subscribe();
          }
        })
      )
      .subscribe({
        next: user => this.fillForms(user, customer)
      });
  }

  private formatCPF(cpf: string | number): string {
    const value = cpf.toString().replace(/\D/g, ''); // Remove tudo que não for número
    if (value.length !== 11) {
      return '';
    }
    return value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }

  private formatCNPJ(cnpj: string | number): string {
    const value = cnpj.toString().replace(/\D/g, ''); // Remove tudo que não for número
    if (value.length !== 14) {
      return '';
    }
    return value.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  }

  private fillForms(user: User, customer: Customer) {
    if (user) {
      customer = customer || ({ default_payment: {} } as Customer);
      this.payableWith = (this.currency === 'BRL' ? this.currentOrder?.payment_method : null) || PaymentMethodsEnum.CreditCard;

      if (customer.id) {
        const customerCnpj = customer.cnpj?.replace(/[^0-9]/, '');
        this.isLegalPerson = customerCnpj ? customerCnpj.length >= 14 : !!user.cnpj;
      }

      this.form.get('customer.email').setValue(this.form.get('customer.email').value || customer.email || user.email);
      this.form.get('customer.name').setValue(this.form.get('customer.name').value || customer.name || (this.isLegalPerson ? user.company || '' : user.name));
      this.form.get('customer.name').markAsTouched();

      this.form.get('address.street').setValue(this.form.get('address.street').value || customer.street || '');
      this.form.get('address.number').setValue(this.form.get('address.number').value || customer.number || '');
      this.form.get('address.zip_code').setValue(this.form.get('address.zip_code').value || customer.zip_code || '');
      this.form.get('address.state').setValue(this.form.get('address.state').value || customer.state || '');
      this.form.get('address.country').setValue(this.form.get('address.country').value || this.country);

      if (this.isBrl()) {
        this.form.get('customer.cpf').setValue(this.formatCPF(this.isLegalPerson ? '' : this.form.get('customer.cpf').value || customer.cpf || user.cpf || ''));
        this.form.get('customer.cnpj').setValue(this.formatCNPJ(this.isLegalPerson ? this.form.get('customer.cnpj').value || customer.cnpj || user.cnpj || '' : ''));

        this.form.get('address.complement').setValue(this.form.get('address.complement').value || customer.complement || '');
        this.form.get('address.district').setValue(this.form.get('address.district').value || customer.district || '');
        this.form.get('address.city').setValue(this.form.get('address.city').value || customer.city || '');
        this.form.get('address.ibge_code').setValue(this.form.get('address.ibge_code').value || customer.ibge_code || '');
      }
    }
  }
}
